<template>
	<div class="bb-panel-wrapper">
		<!-- Export Email -->
		<section class="export-section export-email" v-show="exportEmailAllowed">
			<span class="b360-form-label" role="heading">Send</span>
			<div class="export-card-container b360-grid-row">
				<ExportCard
					icon="users-medical"
					icon-weight="fal"
					card-text="Send to Contact"
					export-type="email"
					class="b360-col-12"
					@click.native="handleEmailSend"
				/>
			</div>
		</section>
		<!-- Flyer -->
		<section class="export-section" v-show="selectedMediaType !== 3">
			<span class="b360-form-label" role="heading">Export</span>
			<p class="section-instructions">Select format for instant download.</p>
			<div class="export-card-container b360-grid-row">
				<ExportCard
					v-for="(card, index) in exportTypeArray"
					:icon="card.icon"
					:icon-weight="card.iconWeight"
					:export-type="card.exportType"
					:card-text="card.cardText"
					:key="index"
					:filePath="card.filePath"
					class="b360-col-12"
					@click.native="addPartnerPoints(card.exportType)"
				/>
			</div>
		</section>
		<!-- Social Post -->
		<section class="share-section" v-show="selectedMediaType === socialPostId">
			<div class="social-share-container">
				<SocialBar
					@click="
						validateShowModal();
						$emit('modal-open', true);
					"
					v-for="(socialType, index) in socialArray"
					:socialType="socialType"
					:icon="socialType.icon"
					:icon-weight="socialType.iconWeight"
					:social-name="socialType.socialName"
					:key="index"
				/>
			</div>
		</section>
		<SocialComposer
			v-show="selectedMediaType === socialPostId"
			:mediaData="mediaData"
			:isBB="true"
			ref="socialComposer"
			@socialComposerClose="handleSocialComposerClose"
		/>
		<Modal
			:modalTitle="exportEmailModal.caption"
			actionBtnName="OK"
			class="md email-confirmation-modal"
			v-if="exportEmailModal.show"
			@close="exportEmailModal.show = false"
			@clicked="exportEmailModal.show = false"
		>
			<div slot="modalContent" class="modal-slot">
				<font-awesome-icon
					v-if="exportEmailModal.caption === 'Success'"
					class="b360-form-icon"
					:icon="['far', 'check-circle']"
				/>
				{{ exportEmailModal.message }}
			</div>
		</Modal>
		<SelectRecipientModal
			v-if="
				showContactSelectModal && exportEmailAllowed && !clientConnectAlertId
			"
			@close="handleRecipientModalClose"
			@onSend="handleCCv2SendEmail"
			:brandBuilderSendEmailError="brandBuilderSendEmailError"
			:response="brandBuilderSendEmailResponse"
			:waiting="waiting"
			:success="success"
			:emailSent="emailSent"
		/>
		<BaseModal
			class="email-modal"
			:showFooter="false"
			@close="openSendEmailModal = false"
			v-if="openSendEmailModal && clientConnectAlertId"
		>
			<div slot="modalBody">
				<LoadingIcon
					v-if="!emailSent && !brandBuilderSendEmailError"
					alignment="center"
				/>
				<FallBack
					v-else-if="emailSent"
					:message="brandBuilderSendEmailResponse"
					:border="false"
					iconStyle="far"
					icon="at"
				/>
				<FallBack
					v-else-if="brandBuilderSendEmailError"
					:message="brandBuilderSendEmailError"
					:border="false"
					iconStyle="fal"
					icon="exclamation-triangle"
				/>
			</div>
		</BaseModal>
	</div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import * as htmlToImage from 'html-to-image';
import Modal from '@/components/global/Modal.vue';
import ExportCard from '@/components/brand-builder/ExportCard.vue';
import SocialBar from '@/components/brand-builder/SocialBar.vue';
import partnerPointApi from '@/services/Lopp/partner-points-service';
import SelectRecipientModal from './SelectRecipientModal/SelectRecipientModal.vue';
import BaseModal from '../../../components/global/BaseModal.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import FallBack from '../../../components/global/FallBack.vue';
import communicationService from '../../../services/toolbox/communication-service';
import SocialComposer from '@/components/global/SocialComposer.vue';
import { SEND_EMAIL_MESSAGE } from './constants/BBExport-constants';
import { STATUS_ACTION_TYPES } from '../../client-connect-v2/constants';
import alertsService from '@/services/contacts/alerts-service.js';

import { isEmpty } from 'lodash';

export default {
	name: 'BbExport',
	components: {
		FallBack,
		LoadingIcon,
		Modal,
		ExportCard,
		SocialBar,
		SelectRecipientModal,
		BaseModal,
		SocialComposer,
	},
	data() {
		return {
			exportEmailAllowed: false,
			exportEmailModal: {
				show: false,
				caption: 'Success',
				message: '',
			},
			mediaData: [],
			pricingFlyerId: 6,
			showContactSelectModal: false,
			showError: false,
			socialPostId: 2,
			socialSelected: [],
			waiting: false,
			success: false,
			emailSent: false,
			isPricingFlyer: parseInt(this.$route.params.campaignId, 10) === 52,
			recipients: [],
			brandBuilderSendEmailError: null,
			brandBuilderSendEmailResponse: null,
			openSendEmailModal: false,
			emailFilters: {
				contactsIds: [],
				typesIds: [],
				searchTerm: '',
			},
		};
	},
	computed: {
		...mapGetters([
			'currentUserRolodexIds',
			'isImpersonated',
			'currentImpersonatedUserRolodexId',
			'partnerPointsProfile',
		]),
		...mapState({
			exportTypeArray: (state) => state.brandBuilderEditor.exportTypeArray,
			socialArray: (state) => state.brandBuilderEditor.socialArray,
			selectedMediaType: (state) => state.brandBuilderEditor.selectedMediaType,
			manualEmailData: (state) => state.brandBuilderEditor.manualEmailData,
			currentProfile: (state) => state.profileSwitcher.profileSelector,
			clientConnectAlertEmailDetails: (state) =>
				state.brandBuilderEditor.clientConnectAlertEmail,
			clientConnectAlertId: (state) =>
				state.brandBuilderEditor.clientConnectAlertEmail.alertId,
			subjectLine: (state) => state.brandBuilderEditor.subjectLine,
		}),
	},
	methods: {
		...mapActions([
			'selectSocialType',
			'resetPricingFlyerData',
			'toggleLoader',
		]),
		async addPartnerPoints(exportType) {
			const { campaignType, mediaType } =
				this.partnerPointsProfile();

			await partnerPointApi.toolboxAddPointsAsync(
				campaignType,
				mediaType,
				exportType,
			);
		},
		handleCCv2SendEmail({
			recipients,
			sendAll = false,
			contactsIds,
			typesIds,
			searchTerm,
		}) {
			this.emailFilters.typesIds = typesIds;
			this.emailFilters.contactsIds = contactsIds;
			this.emailFilters.searchTerm = searchTerm;
			if (sendAll) {
				this.recipients = recipients;
				this.sendEmail(sendAll);
			} else {
				this.recipients = recipients;
				this.sendEmail();
			}
		},
		handleRecipientModalClose() {
			this.showContactSelectModal = false;
			this.success = false;
			this.waiting = false;
			this.emailSent = false;
			this.brandBuilderSendEmailError = null;
			this.brandBuilderSendEmailResponse = null;
		},
		clearPricingFlyer() {
			if (this.isPricingFlyer) {
				this.resetPricingFlyerData();
			}
		},

		handleSocialBarClick(data, socialName) {
			const socialType = socialName.toLowerCase();
			const index = this.socialSelected.indexOf(socialType);
			if (data === true && index === -1) {
				// add to social array if not present
				this.socialSelected.push(socialType);
			} else if (index !== -1) {
				// remove from social array if present
				this.socialSelected.splice(index, 1);
			}
			this.showError = false;
		},

		validateShowModal() {
			this.showError = false;
			this.getDefaultMediaUrl();
			this.$refs.socialComposer.validateShowModal();
		},

		closeContactSelectModal() {
			this.showContactSelectModal = false;
		},
		handleSocialComposerClose() {
			this.$emit('modal-open', false);
		},
		getDefaultMediaUrl() {
			const thisRef = this;
			const mediaDiv = document.getElementById('item-container');

			if (mediaDiv !== null) {
				if (isEmpty(thisRef.mediaData)) {
					/*
					The mediaDiv needs to be fullscale before we convert it to a .jpg.

					If it is not at fullscale htmlToImage will create a malformatted image.

					If we don't remove transitions from mediaDiv, It will be passed to htmlToImage partially
					through it's transition to fullscale and therfore the .jpg will be malformatted.

					We apply a global .no-transition class to mediaDiv to overide transitions,

					so that way it becomes fullscale instantly.

					In order for the class to work properly we need to trigger a reflow of the page and flush all CSS.

				 	Calling mediaDiv.offsetHeight triggers a reflow.

					This causes the page to apply the no-transtion class and prevents

					transitions from playing on the mediaDiv

					After the conversion to a .jpg we remove the no-transition class.
				*/
					mediaDiv.classList.add('no-transition');
					// Triggers reflow and flushes CSS.
					mediaDiv.offsetHeight;
					mediaDiv.classList.add('fullscale');
					htmlToImage
						.toJpeg(mediaDiv, {
							backgroundColor: '#fff',
							quality: 1,
							pixelRatio: 1,
						})
						.then((dataUrl) => {
							// This allows the title card to scale to 1 for the video, instead of using the viewport scale.
							mediaDiv.classList.remove('no-transition');
							mediaDiv.classList.remove('fullscale');
							thisRef.mediaData = [dataUrl];
						});
				}
			}
		},

		handleEmailSend() {
			if (this.clientConnectAlertId) {
				this.openSendEmailModal = true;
				this.sendEmail();
			}
			this.showContactSelectModal = true;
		},
		//TO-DO: pass boolean
		async sendEmail(sendAllEmail = false) {
			const bodyText =
				this.selectedMediaType === 6
					? document.getElementById('pricingPreviewTemplate').innerHTML
					: document.getElementById('emailPreviewTemplate').innerHTML;

			const style = getComputedStyle(document.body);
			const bodyTextNoVars = bodyText
				.replace(
					/var\(?--primary-color\)?/g,
					style.getPropertyValue('--primary-color'),
				)
				.replace(
					/var\(?--primary-color-transparent\)?/g,
					style.getPropertyValue('--primary-color-transparent'),
				)
				.replace(
					/var\(?--secondary-color\)?/g,
					style.getPropertyValue('--secondary-color'),
				)
				.replace(
					/var\(?--secondary-color-transparent\)?/g,
					style.getPropertyValue('--secondary-color-transparent'),
				)
				.replace(
					/var\(?--tertiary-color\)?/g,
					style.getPropertyValue('--tertiary-color'),
				)
				.replace(
					/var\(?--tertiary-color-transparent\)?/g,
					style.getPropertyValue('--tertiary-color-transparent'),
				)
				.replace(
					/var\(?var(--base-font)\)?/g,
					`'${style.getPropertyValue('--base-font')}', Arial, sans-serif`,
				);

			const { campaignId } = this.$route.params;
			const { entityId, contactsIds } = !this.isImpersonated
				? this.currentUserRolodexIds
				: this.currentImpersonatedUserRolodexId;

			const payload = {
				brokerEmailAddress: this.currentProfile.userEmailAddress,
				brokerName: this.currentProfile.userPreferredName,
				subject: this.subjectLine,
				bodyText: bodyTextNoVars,
				campaignId,
				alertId:
					this.clientConnectAlertId === null
						? null
						: parseInt(this.clientConnectAlertId, 10),
				sendAllEmail,
			};
			const recipients = this.clientConnectAlertId
				? [
						{
							leadId: parseInt(this.clientConnectAlertEmailDetails.leadId, 10),
							contactEmailAddress: this.clientConnectAlertEmailDetails.email,
							contactFirstName:
								this.clientConnectAlertEmailDetails.borrowerFirstName,
							contactLastName:
								this.clientConnectAlertEmailDetails.borrowerLastName,
						},
				  ]
				: this.recipients;
			if (sendAllEmail) {
				payload.excludedRecipients = recipients;
				payload.searchContactInfo = {
					searchTerm: this.emailFilters.searchTerm,
					data: {
						entityId,
						contactsIds: isEmpty(this.emailFilters.contactsIds)
							? contactsIds
							: this.emailFilters.contactsIds,
						typesIds: this.emailFilters.typesIds,
					},
				};
			} else {
				payload.recipients = recipients;
				payload.searchContactInfo = {
					searchTerm: this.emailFilters.searchTerm,
					data: {
						contactsIds: this.emailFilters.contactsIds,
						typesIds: this.emailFilters.typesIds,
					},
				};
			}
			try {
				this.waiting = true;
				let response;
				let errorList;

				if (sendAllEmail) {
					response = await communicationService.sendToAll(payload);
					if (response && response.data === true) {
						this.brandBuilderSendEmailResponse =
							SEND_EMAIL_MESSAGE.SEND_ALL_SUCCESS;
						this.emailSent = true;
						this.brandBuilderSendEmailError = null;
						this.addPartnerPoints('Email');
					} else {
						this.handleSendEmailError(SEND_EMAIL_MESSAGE.ERROR);
					}
				} else {
					response = await communicationService.sendEmail(payload);
					if (response && response.data) {
						errorList = response.data.filter((obj) => obj.responses.hasErrors);
					}
					if (errorList && errorList.length === 0) {
						this.brandBuilderSendEmailResponse = SEND_EMAIL_MESSAGE.SUCCESS;
						this.brandBuilderSendEmailError = null;
						this.waiting = false;
						this.success = true;
						setTimeout(() => {
							this.emailSent = true;
						}, 1000);
						this.addPartnerPoints('Email');
						// if the email is sent sucessfully for a CCv2 alert set the alert status to done
						// this.clientConnectAlertId will always have a value when the email being sent is for CCv2 alert
						if (this.clientConnectAlertId) {
							await alertsService.updateAlertStatus({
								alertIds: [parseInt(this.clientConnectAlertId, 10)],
								statusId: STATUS_ACTION_TYPES.DONE,
							});
						}
					} else {
						this.handleSendEmailError(SEND_EMAIL_MESSAGE.ERROR);
					}
				}
			} catch (error) {
				this.handleSendEmailError(SEND_EMAIL_MESSAGE.ERROR);
			}
		},
		handleSendEmailError(errorMessage) {
			this.brandBuilderSendEmailError = errorMessage;
			this.brandBuilderSendEmailResponse = null;
			this.waiting = false;
			this.success = false;
		},
	},
	mounted() {
		const thisRef = this;

		if (this.selectedMediaType === 3) {
			// TODO ADD PERMISSIONS CHECK
			this.exportEmailAllowed = true;
		}

		// sync social selected if navigated away
		this.socialSelected = [...this.socialArray]
			.filter((e) => e.selected)
			.map((e) => e.socialName.toLowerCase());
	},
};
</script>

<style lang="scss" scoped>
@media only screen and (min-width: ($breakpoint+1px)) {
	.bb-panel-wrapper {
		max-width: $brand-builder-sub-view-with;
	}
}

.export-card-container {
	@include rem(grid-gap, 20px);
	@include rem(margin-bottom, 20px);
}

.social-share-container {
	@include rem(margin-bottom, 30px);
}

.social-bar {
	& + & {
		@include rem(margin-top, 10px);
	}
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity $b360-motion-duration--shortest
		$b360-motion-easing--standard;
}

.fade-enter,
.fade-leave-active {
	opacity: 0;
}

.mini-calendar-container {
	margin: 0 auto;
}

.social-share-error {
	@include rem(margin-bottom, 16px);
	color: $error;
}

.export-email {
	@include rem(margin-bottom, 25px);
}

.modal-slot {
	text-align: center;

	.b360-form-icon {
		@include rem(font-size, 32px);
		@include rem(margin, auto auto 10px);
		display: block;
	}
}

.email-modal {
	/deep/ .modal-dialog {
		top: 90px;
	}
}
</style>
